import { getData } from "@/lib/utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isUndefined } from "lodash";

// Define the Product interface
interface Product {
  quatationId: any;
  productId: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  image?: string | null;
  company: {
    CompanyNr: string;
    id: string;
    nameAr: string;
  };
  tender: { title: string; id: string; status: string };
}

// Define the TenderStatus type
export type TenderStatus =
  | "Payment"
  | "Planning"
  | "Finished"
  | "Sending"
  | "Receiving"
  | "Closed"
  | "Opened"
  | "Accepted"
  | "PaymentConfirmation"
  | null;

// Define the Tender interface
interface Tender {
  id: string | null;
  TenderNr: number | null;
  title: string;
  status: TenderStatus;
  addressId: string | null;
  value: number;
  endDate: string;
  openingDate: string;
  type: string;
  region: string;
  city: string;
  Paylater: string;
  categoriesIds: string[];
  fileId: File | null;
  products: Array<{
    item: string;
    quantity: number;
    notice: string;
    unit: string;
    attachment: File | null;
  }>;
  companyId: string | null;
}

// Define the Quotation interface
interface Quotation {
  products: {
    productId: string;
    unitName: string;
    quantity: number;
    unitPrice: number;
    vat: number;
    notice: string;
    attachment?: File | null;
    image?: { url: string } | null | undefined;
  }[];
  selectedItems?: string[];
}

// Define the AuthState interface
export interface AuthState {
  authUser: any | null;
  loadUser: boolean;
  tender: Tender;
  categories: any[];
  quatation: Quotation | null;
  myCompany: any | null;
  cart: Product[];
}

// Define the initial state
const initialState: AuthState = {
  authUser: isUndefined(getData("userInfo")) ? null : getData("userInfo"),
  myCompany: null,
  quatation: {
    products: [],
    selectedItems: [],
  },
  tender: {
    addressId: null,
    id: null,
    TenderNr: null,
    title: "",
    status: null,
    value: 0,
    endDate: "",
    openingDate: "",
    type: "Tiny",
    region: "",
    city: "",
    Paylater: "No",
    categoriesIds: [],
    fileId: null,
    products: [],
    companyId: null,
  },
  categories: [],
  loadUser: false,
  cart: [],
};

// Create the slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateQuatation(state, action: PayloadAction<Partial<Quotation>>) {
      if (state.quatation) {
        state.quatation = { ...state.quatation, ...action.payload };
      } else {
        state.quatation = {
          products: [],
          selectedItems: [],
          ...action.payload,
        };
      }
    },
    toggleSelectedItem(state, action: PayloadAction<string>) {
      if (state.quatation) {
        const selectedItems = state.quatation.selectedItems || [];
        const index = selectedItems.indexOf(action.payload);
        if (index === -1) {
          selectedItems.push(action.payload);
        } else {
          selectedItems.splice(index, 1);
        }
        state.quatation.selectedItems = selectedItems;
      }
    },
    toggleAllItems(state, action: PayloadAction<string[]>) {
      if (state.quatation) {
        state.quatation.selectedItems = action.payload;
      }
    },
    updateTender(state, action: PayloadAction<Partial<Tender>>) {
      state.tender = { ...state.tender, ...action.payload };
    },
    clearTender(state) {
      state.tender = initialState.tender;
    },
    updateAuthUser(state, action: PayloadAction<any>) {
      state.authUser = action.payload;
      state.loadUser = true;
    },
    updateLoadUser(state, action: PayloadAction<boolean>) {
      state.loadUser = action.payload;
    },
    getMyCompany(state, action: PayloadAction<any>) {
      state.myCompany = action.payload;
    },
    getCategories(state, action: PayloadAction<any>) {
      state.categories = action.payload;
    },
    addToCart(state, action: PayloadAction<Product>) {
      // Check if the product already exists in the cart
      const existingProductIndex = state.cart.findIndex(
        (item) => item.productId === action.payload.productId
      );

      if (existingProductIndex >= 0) {
        // If it exists, update the quantity
        state.cart[existingProductIndex].quantity += action.payload.quantity;
      } else {
        // If it doesn't exist, add it to the cart
        state.cart.push(action.payload);
      }
    },
    updateCartItemQuantity(
      state,
      action: PayloadAction<{ id: string; quantity: number }>
    ) {
      const item = state.cart.find(
        (item) => item.productId === action.payload.id
      );
      if (item && action.payload.quantity > 0) {
        item.quantity = action.payload.quantity;
      }
    },
    removeFromCart(state, action: PayloadAction<string>) {
      // Remove the product by ID
      state.cart = state.cart.filter(
        (item) => item.productId !== action.payload
      );
    },
    EmptyCart(state) {
      // Remove the product by ID
      state.cart = [];
    },
  },
});

// Export actions
export const {
  updateAuthUser,
  updateTender,
  updateQuatation,
  clearTender,
  updateLoadUser,
  getMyCompany,
  getCategories,
  addToCart,
  EmptyCart,
  removeFromCart,
  updateCartItemQuantity,
  toggleSelectedItem,
  toggleAllItems,
} = authSlice.actions;

// Export the reducer and the AuthState type
export default authSlice.reducer;
